import { shouldRetryStatus, StreamHttpError } from './stream-http';

const BASE_RETRY_DELAY_MS = 1_000;
const MAX_RETRY_DELAY_MS = 15_000;

export type StreamRetryDecision =
  | { type: 'retry'; error: Error; nextAttempt: number; delayMs: number }
  | { type: 'stop'; error: Error };

export const decideStreamRetry = (
  error: unknown,
  attempt: number,
  authRefreshed = false
): StreamRetryDecision => {
  const normalized = error instanceof Error ? error : new Error(String(error));

  // A non-retryable HTTP status (e.g. 404 = run not found / no access) is
  // terminal — the run's stream genuinely isn't available, so give up.
  if (
    error instanceof StreamHttpError &&
    !shouldRetryStatus(error.status) &&
    !(authRefreshed && error.status === 401)
  ) {
    return { type: 'stop', error: normalized };
  }

  // Transient failures (5xx, network drops, "closed before a boundary", LB
  // connection-age cuts) retry indefinitely with capped exponential backoff.
  // We do NOT cap the attempt count: the server supports mid-turn resume via
  // Last-Event-ID, and the caller's `enabled` gate (run still active) bounds
  // how long we keep trying — so a long turn cut by infra reconnects instead
  // of stranding the stream in a permanent error state.
  const nextAttempt = attempt + 1;
  const delayMs = Math.min(BASE_RETRY_DELAY_MS * 2 ** attempt, MAX_RETRY_DELAY_MS);
  return { type: 'retry', error: normalized, nextAttempt, delayMs };
};
